import { Component, ChangeDetectorRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <div class="auth-container">
      <div class="scryber-icon-row">
        <img src="/scryber-logo-lime.png?v=4" alt="Scryber" />
      </div>
      <div class="auth-card">
        <div class="auth-header">
          <h1>Sign in to Scryber</h1>
        </div>

        @if (errorMessage) {
          <div class="auth-error" role="alert">
            {{ errorMessage }}
          </div>
        }

        <form [formGroup]="form" (ngSubmit)="onSubmit()" class="auth-form">
          <div class="form-group">
            <label for="email">Email</label>
            <input id="email" type="email" formControlName="email" placeholder="you@example.com" autocomplete="email" />
            @if (form.get('email')?.invalid && form.get('email')?.touched) {
              <span class="field-error">Valid email is required</span>
            }
          </div>
          <div class="form-group">
            <div class="label-row">
              <label for="password">Password</label>
              <button type="button" class="auth-link inline auth-link-button" (click)="goToForgotPassword()">
                Forgot your password?
              </button>
            </div>
            <input id="password" type="password" formControlName="password" placeholder="••••••••" autocomplete="current-password" />
            @if (form.get('password')?.invalid && form.get('password')?.touched) {
              <span class="field-error">Password is required</span>
            }
          </div>
          <button type="submit" class="auth-button primary" [disabled]="form.invalid || loading">
            {{ loading ? 'Signing in…' : 'Sign in' }}
            <span class="btn-arrow">→</span>
          </button>
        </form>

        <div class="auth-divider" role="separator" aria-label="Or sign in with">
          <span>OR</span>
        </div>

        <button
          type="button"
          class="auth-button oauth"
          aria-label="Sign in with Google"
          [disabled]="loading"
          (click)="signInWithGoogle()">
          <svg class="google-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign in with Google
        </button>

        <div class="auth-footer">
          <p>Don't have an account? <button type="button" class="auth-link auth-link-button" (click)="goToSignup()">Sign up</button></p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .auth-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      min-height: 100vh;
      background: linear-gradient(160deg, var(--scryber-rail-from) 0%, var(--scryber-rail-to) 55%, #022c22 100%);
      padding: 0 0 20px;
    }
    :host-context(html.capacitor-native) .auth-container {
      padding-top: env(safe-area-inset-top, 0px);
      padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px));
    }
    .auth-card {
      background: white;
      border-radius: 12px;
      box-shadow: 0 20px 40px rgba(6, 78, 59, 0.25);
      padding: 40px;
      width: calc(100% - 40px);
      max-width: 560px;
      margin: auto;
    }
    .auth-header h1 {
      color: var(--scryber-text);
      font-size: 24px;
      font-weight: 700;
      margin: 0 0 24px 0;
      font-family: 'Poppins', 'Inter', sans-serif;
    }
    .auth-error {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 14px;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      color: #b91c1c;
      font-size: 14px;
      margin-bottom: 20px;
    }
    .auth-form { display: flex; flex-direction: column; gap: 18px; }
    .form-group { display: flex; flex-direction: column; gap: 6px; }
    .form-group label { color: #374151; font-size: 14px; font-weight: 500; }
    .label-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 4px;
    }
    .auth-link.inline { font-size: 13px; }
    .form-group input {
      padding: 10px 12px;
      font-size: 16px;
      border: 1px solid var(--scryber-border);
      border-radius: 6px;
      background: white;
      color: #1f2937;
    }
    .form-group input::placeholder { color: #9ca3af; }
    .form-group input:focus {
      outline: none;
      border-color: var(--scryber-green-600);
      box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.25);
    }
    .field-error { font-size: 12px; color: #b91c1c; }
    .auth-button {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
      padding: 12px 24px;
      font-size: 16px;
      font-weight: 600;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-top: 4px;
      touch-action: manipulation;
      -webkit-tap-highlight-color: rgba(5, 150, 105, 0.15);
    }
    .auth-button.primary {
      background: linear-gradient(135deg, var(--scryber-green-600) 0%, var(--scryber-rail-to) 100%);
      color: white;
    }
    .auth-button.primary:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 8px 25px rgba(5, 150, 105, 0.4);
    }
    .auth-button:disabled { opacity: 0.7; cursor: not-allowed; }
    .btn-arrow { font-size: 18px; }
    .auth-divider {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 22px 0 16px;
      color: #9ca3af;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.06em;
    }
    .auth-divider::before,
    .auth-divider::after {
      content: '';
      flex: 1;
      height: 1px;
      background: var(--scryber-border);
    }
    .auth-button.oauth {
      background: #fff;
      color: #374151;
      border: 1px solid #d1d5db;
      text-decoration: none;
      margin-top: 0;
      font-family: inherit;
    }
    .auth-button.oauth:hover {
      background: #f9fafb;
      border-color: #9ca3af;
      transform: none;
      box-shadow: none;
    }
    .google-icon {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }
    .auth-footer {
      margin-top: 24px;
      padding-top: 24px;
      border-top: 1px solid var(--scryber-border);
      text-align: center;
    }
    .auth-footer p { color: #718096; font-size: 14px; margin: 0; }
    .auth-link { color: var(--scryber-green-600); font-weight: 500; text-decoration: none; touch-action: manipulation; }
    .auth-link:hover { text-decoration: underline; }
    .auth-link-button {
      background: none;
      border: none;
      padding: 0;
      font: inherit;
      cursor: pointer;
      -webkit-tap-highlight-color: rgba(5, 150, 105, 0.15);
    }
    .auth-footer .auth-link-button {
      display: inline;
      min-height: 44px;
      line-height: 1.4;
      vertical-align: baseline;
    }
    .label-row .auth-link-button {
      min-height: 44px;
      padding: 0.5rem 0;
    }
    @media (max-width: 480px) { .auth-card { padding: 24px; } }
  `],
})
export class LoginComponent implements OnInit, OnDestroy {
  /** Spring Boot: `spring.security.oauth2.client.registration.google` → `/oauth2/authorization/google` */
  private readonly googleSignInHref = '/oauth2/authorization/google';
  /** Secret for this sign-in. Stays in the page; only its hash is sent to start Google. */
  private oauthVerifier: string | null = null;

  form: FormGroup;
  loading = false;
  errorMessage = '';
  private formChangesSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.formChangesSub = this.form.valueChanges.subscribe(() => this.cdr.markForCheck());
    this.formChangesSub.add(this.form.statusChanges.subscribe(() => this.cdr.markForCheck()));
    window.addEventListener('scryber-oauth', this.onNativeOAuth);

    const urlTree = this.router.parseUrl(this.router.url);
    const raw = urlTree.queryParams['message'];
    const msg = raw != null ? String(raw).trim() : '';
    if (msg) {
      this.errorMessage = msg;
      delete urlTree.queryParams['message'];
      delete urlTree.queryParams['error'];
      this.router.navigateByUrl(urlTree, { replaceUrl: true });
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.formChangesSub?.unsubscribe();
    window.removeEventListener('scryber-oauth', this.onNativeOAuth);
  }

  signInWithGoogle(): void {
    if (this.loading) {
      return;
    }
    if (!Capacitor.isNativePlatform()) {
      window.location.assign(this.googleSignInHref);
      return;
    }
    void this.startNativeGoogleSignIn();
  }

  private async startNativeGoogleSignIn(): Promise<void> {
    const webkit = (
      window as unknown as {
        webkit?: { messageHandlers?: { scryberGoogleAuth?: { postMessage: (body: string) => void } } };
      }
    ).webkit;
    let startUrl = '';
    try {
      const verifier = this.createOauthVerifier();
      const challenge = await this.sha256Base64Url(verifier);
      this.oauthVerifier = verifier;
      startUrl = this.nativeGoogleAuthUrl(challenge);
    } catch {
      this.oauthVerifier = null;
      this.errorMessage = 'Google sign-in is not available in this build.';
      this.cdr.markForCheck();
      return;
    }
    if (!startUrl || !webkit?.messageHandlers?.scryberGoogleAuth) {
      this.oauthVerifier = null;
      this.errorMessage = 'Google sign-in is not available in this build.';
      this.cdr.markForCheck();
      return;
    }
    this.errorMessage = '';
    this.loading = true;
    this.cdr.markForCheck();
    webkit.messageHandlers.scryberGoogleAuth.postMessage(startUrl);
  }

  private nativeGoogleAuthUrl(codeChallenge: string): string {
    const configured = (environment.backendUrl ?? '').replace(/\/$/, '');
    const origin =
      configured || (window.location.protocol === 'capacitor:' ? '' : window.location.origin);
    if (!origin) {
      return '';
    }
    const challenge = encodeURIComponent(codeChallenge);
    return `${origin}/oauth2/authorization/google?client=ios&code_challenge=${challenge}`;
  }

  /** 32 random bytes, base64url, no padding. RFC 7636 code_verifier. */
  private createOauthVerifier(): string {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return this.bytesToBase64Url(bytes);
  }

  private async sha256Base64Url(value: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return this.bytesToBase64Url(new Uint8Array(digest));
  }

  private bytesToBase64Url(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private onNativeOAuth = (event: Event): void => {
    const detail = (event as CustomEvent<{ url?: string; error?: string; cancelled?: boolean }>).detail ?? {};
    this.ngZone.run(() => {
      if (detail.cancelled) {
        this.oauthVerifier = null;
        this.loading = false;
        this.cdr.markForCheck();
        return;
      }
      if (detail.error && !detail.url) {
        this.oauthVerifier = null;
        this.loading = false;
        this.errorMessage = detail.error;
        this.cdr.markForCheck();
        return;
      }
      let code: string | null = null;
      let message: string | null = null;
      try {
        const parsed = new URL(detail.url ?? '');
        code = parsed.searchParams.get('code');
        message = parsed.searchParams.get('message');
      } catch {
        message = 'Google sign-in failed';
      }
      const verifier = this.oauthVerifier;
      this.oauthVerifier = null;
      if (!code || !verifier) {
        this.loading = false;
        this.errorMessage = message || 'Google sign-in failed';
        this.cdr.markForCheck();
        return;
      }
      this.auth.exchangeNativeOAuthCode(code, verifier).subscribe({
        next: () => this.router.navigate(['/']),
        error: (err) => {
          this.loading = false;
          const body = err && (err as { error?: { message?: string } }).error;
          this.errorMessage = body?.message || 'Google sign-in failed. Try again.';
          this.cdr.markForCheck();
        },
      });
    });
  };

  goToSignup(): void {
    void this.router.navigate(['/signup']);
  }

  goToForgotPassword(): void {
    void this.router.navigate(['/forgot-password']);
  }

  onSubmit(): void {
    if (this.form.invalid || this.loading) return;
    this.errorMessage = '';
    this.loading = true;
    this.cdr.markForCheck();
    this.auth.login(this.form.getRawValue()).subscribe({
      next: () => this.router.navigate(['/']),
      error: (err) => {
        this.loading = false;
        const body = err && (err as any).error;
        const msg =
          (body && typeof body === 'object' && (body.message || body.error)) ||
          (typeof body === 'string' ? body : null) ||
          err?.message;
        this.errorMessage = msg && String(msg).trim() ? String(msg).trim() : 'Invalid email or password. Please try again.';
        this.cdr.markForCheck();
      },
    });
  }
}
